import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Package, Truck, Clock, CheckCircle, MapPin } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STEPS = ["confirmed", "processing", "shipped", "delivered", "refunded", "cancelled"];
const STATUS_LABELS = { confirmed: "Confirmed", processing: "Processing", shipped: "Shipped", delivered: "Delivered", refunded: "Refunded", cancelled: "Cancelled" };
const STATUS_COLORS = {
  confirmed: "text-[#39FF14] border-[#39FF14]/30 bg-[#39FF14]/10",
  processing: "text-yellow-400 border-yellow-400/30 bg-yellow-400/10",
  shipped: "text-blue-400 border-blue-400/30 bg-blue-400/10",
  delivered: "text-[#39FF14] border-[#39FF14]/30 bg-[#39FF14]/10",
  refunded: "text-orange-400 border-orange-400/30 bg-orange-400/10",
  cancelled: "text-red-400 border-red-400/30 bg-red-400/10",
};

export default function AdminOrdersPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updateModal, setUpdateModal] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [updating, setUpdating] = useState(false);

  const fetchOrders = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/orders`);
      setOrders(data);
    } catch (e) {
      console.error("Failed to load orders", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== "admin") { navigate("/"); return; }
    fetchOrders();
  }, [user, authLoading, navigate, fetchOrders]);

  const handleUpdateStatus = async () => {
    if (!updateModal || !newStatus) return;
    setUpdating(true);
    try {
      const body = { status: newStatus };
      if (trackingNumber.trim()) body.tracking_number = trackingNumber.trim();
      await axios.put(`${API}/api/admin/orders/${updateModal.id}/status`, body);
      setUpdateModal(null);
      setNewStatus("");
      setTrackingNumber("");
      fetchOrders();
    } catch (e) {
      console.error("Update failed", e);
    } finally {
      setUpdating(false);
    }
  };

  const openUpdateModal = (order) => {
    setUpdateModal(order);
    setNewStatus(order.status);
    setTrackingNumber(order.tracking_number || "");
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#050505" }}>
        <div className="w-8 h-8 border border-[#39FF14] border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#050505" }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <p className="font-mono uppercase tracking-[0.3em] text-xs font-bold text-[#39FF14] mb-2">Admin</p>
            <h1 data-testid="admin-orders-heading" className="text-4xl sm:text-5xl font-black uppercase tracking-tighter text-[#F2F2F2]" style={{ fontFamily: "Chivo, sans-serif" }}>
              Order Manager
            </h1>
          </div>
          <div className="flex gap-3">
            <Button onClick={() => navigate("/admin")} variant="outline" className="bg-transparent border-[#1A1A1A] hover:border-[#39FF14] text-[#A1A1A1] hover:text-[#39FF14] rounded-none font-mono text-xs uppercase tracking-wider">
              Products
            </Button>
          </div>
        </div>

        {orders.length === 0 ? (
          <div className="border border-[#1A1A1A] bg-[#0D0D0D] p-16 text-center" data-testid="no-admin-orders">
            <Package className="h-12 w-12 text-[#1A1A1A] mx-auto mb-4" strokeWidth={1} />
            <p className="text-[#A1A1A1]">No orders yet.</p>
          </div>
        ) : (
          <div className="space-y-[1px] bg-[#1A1A1A]">
            {orders.map((order) => {
              const orderDate = new Date(order.created_at);
              const subtotal = Number(order.subtotal ?? order.total ?? 0);
              const shipping_total = Number(order.shipping_total ?? 0);
              const discount = Number(order.discount ?? 0);
              const couponCode = order.coupon?.code;
              const addr = order.shipping_address || {};
              return (
              <div key={order.id} data-testid={`admin-order-${order.id}`} className="bg-[#0D0D0D] p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-4">
                  <div>
                    <p className="font-mono text-xs text-[#A1A1A1] uppercase tracking-wider mb-1">
                      Order #{order.id.slice(-8)}
                    </p>
                    <p className="font-mono text-xs text-[#39FF14]" data-testid={`order-time-${order.id}`}>
                      {orderDate.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                    </p>
                    <p className="font-mono text-xs text-[#F2F2F2] mt-2">{order.user_name || "(no name)"}</p>
                    <p className="font-mono text-xs text-[#A1A1A1]">{order.user_email}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`font-mono text-xs uppercase tracking-wider px-3 py-1 border ${STATUS_COLORS[order.status] || "text-[#A1A1A1] border-[#1A1A1A]"}`}>
                      {STATUS_LABELS[order.status] || order.status}
                    </span>
                    <span className="font-mono text-lg font-bold text-[#39FF14]">${order.total?.toFixed(2)}</span>
                    <Button
                      onClick={() => openUpdateModal(order)}
                      variant="outline"
                      className="bg-transparent border-[#1A1A1A] hover:border-[#39FF14] text-[#A1A1A1] hover:text-[#39FF14] rounded-none font-mono text-[10px] uppercase tracking-wider px-3 py-1 h-auto"
                      data-testid={`update-status-${order.id}`}
                    >
                      Update Status
                    </Button>
                  </div>
                </div>

                {/* Tracking */}
                {order.tracking_number && (
                  <div className="flex items-center gap-2 mb-3">
                    <Truck className="h-3 w-3 text-[#39FF14]" />
                    <span className="font-mono text-xs text-[#A1A1A1]">Tracking: </span>
                    <span className="font-mono text-xs text-[#F2F2F2] font-bold">{order.tracking_number}</span>
                  </div>
                )}

                {/* Shipping address (full) */}
                {addr.full_name && (
                  <div className="flex items-start gap-2 mb-4 p-3 bg-[#050505] border border-[#1A1A1A]" data-testid={`order-address-${order.id}`}>
                    <MapPin className="h-3 w-3 text-[#39FF14] flex-shrink-0 mt-1" strokeWidth={1.5} />
                    <div className="font-mono text-xs text-[#F2F2F2] leading-relaxed">
                      <p className="font-bold">{addr.full_name}</p>
                      <p className="text-[#A1A1A1]">{addr.address_line1}</p>
                      {addr.address_line2 && <p className="text-[#A1A1A1]">{addr.address_line2}</p>}
                      <p className="text-[#A1A1A1]">{addr.city}, {addr.state} {addr.zip_code}</p>
                      <p className="text-[#A1A1A1]">{addr.country || "US"}</p>
                      {addr.phone && <p className="text-[#39FF14] mt-1">📞 {addr.phone}</p>}
                    </div>
                  </div>
                )}

                {/* Itemized breakdown */}
                <div className="bg-[#050505] border border-[#1A1A1A] mb-3" data-testid={`order-items-${order.id}`}>
                  <table className="w-full text-xs font-mono">
                    <thead>
                      <tr className="border-b border-[#1A1A1A]">
                        <th className="px-3 py-2 text-left text-[10px] text-[#39FF14] uppercase tracking-wider">Item</th>
                        <th className="px-3 py-2 text-center text-[10px] text-[#39FF14] uppercase tracking-wider">Qty</th>
                        <th className="px-3 py-2 text-right text-[10px] text-[#39FF14] uppercase tracking-wider">Unit</th>
                        <th className="px-3 py-2 text-right text-[10px] text-[#39FF14] uppercase tracking-wider">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.items?.map((item, idx) => (
                        <tr key={idx} className="border-b border-[#1A1A1A] last:border-b-0">
                          <td className="px-3 py-2 text-[#F2F2F2]">
                            {item.name}
                            {item.selected_color && <span className="text-[#39FF14]"> ({item.selected_color})</span>}
                            {item.color_upcharge && <span className="text-yellow-400 text-[10px]"> +$20 color</span>}
                          </td>
                          <td className="px-3 py-2 text-center text-[#A1A1A1]">{item.quantity}</td>
                          <td className="px-3 py-2 text-right text-[#A1A1A1]">${Number(item.price || 0).toFixed(2)}</td>
                          <td className="px-3 py-2 text-right text-[#39FF14]">${(Number(item.price || 0) * Number(item.quantity || 0)).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Totals breakdown */}
                <div className="flex justify-end" data-testid={`order-totals-${order.id}`}>
                  <div className="text-right space-y-1 font-mono text-xs">
                    <div><span className="text-[#A1A1A1]">Subtotal: </span><span className="text-[#F2F2F2]">${subtotal.toFixed(2)}</span></div>
                    <div><span className="text-[#A1A1A1]">Shipping ({order.shipping_method || "USPS Ground"}): </span><span className="text-[#F2F2F2]">${shipping_total.toFixed(2)}</span></div>
                    {discount > 0 && <div><span className="text-[#A1A1A1]">Discount{couponCode ? ` (${couponCode})` : ""}: </span><span className="text-yellow-400">-${discount.toFixed(2)}</span></div>}
                    <div className="border-t border-[#1A1A1A] pt-1 mt-1"><span className="text-[#39FF14] font-bold uppercase tracking-wider">Total: </span><span className="text-[#39FF14] font-bold text-base">${Number(order.total || 0).toFixed(2)}</span></div>
                  </div>
                </div>

                {/* Stripe ref */}
                {order.stripe_session_id && (
                  <p className="font-mono text-[10px] text-[#A1A1A1]/60 mt-3 truncate" data-testid={`order-stripe-${order.id}`}>Stripe: {order.stripe_session_id}</p>
                )}
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Update Status Dialog */}
      <Dialog open={!!updateModal} onOpenChange={() => setUpdateModal(null)}>
        <DialogContent className="bg-[#0D0D0D] border-[#1A1A1A] rounded-none max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#F2F2F2] font-bold uppercase tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
              Update Order Status
            </DialogTitle>
          </DialogHeader>
          {updateModal && (
            <div className="space-y-4">
              <p className="font-mono text-xs text-[#A1A1A1]">Order #{updateModal.id?.slice(-8)}</p>

              <div className="space-y-2">
                <Label className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#A1A1A1]">Status</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger className="bg-[#050505] border-[#1A1A1A] text-[#F2F2F2] rounded-none" data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0D0D0D] border-[#1A1A1A] rounded-none">
                    {STATUS_STEPS.map(s => (
                      <SelectItem key={s} value={s} className="text-[#F2F2F2] focus:text-[#39FF14] focus:bg-[#141414] rounded-none">
                        {STATUS_LABELS[s]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {(newStatus === "shipped" || trackingNumber) && (
                <div className="space-y-2">
                  <Label className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#A1A1A1]">Tracking Number</Label>
                  <Input
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    placeholder="e.g. 1Z999AA10123456784"
                    className="bg-[#050505] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14]"
                    data-testid="tracking-input"
                  />
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button
                  onClick={handleUpdateStatus}
                  disabled={updating}
                  className="bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono uppercase text-xs font-bold px-6 py-4 disabled:opacity-50"
                  data-testid="confirm-status-update"
                >
                  {updating ? "Updating..." : "Update"}
                </Button>
                <Button
                  onClick={() => setUpdateModal(null)}
                  variant="outline"
                  className="bg-transparent border-[#1A1A1A] hover:border-[#39FF14] text-[#A1A1A1] hover:text-[#39FF14] rounded-none font-mono text-xs uppercase px-6 py-4"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
